import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const HAS_TIMEZONE = /[zZ]|[+-]\d{2}:?\d{2}$/;

/**
 * API often returns naive UTC datetimes (no Z). dayjs would treat those as local
 * and can make freshly-updated timestamps look "in the future", which hides tooltips.
 */
export function parseMilestoneStatusUpdatedAt(value: string): dayjs.Dayjs {
  const trimmed = value.trim();
  if (HAS_TIMEZONE.test(trimmed)) {
    return dayjs(trimmed);
  }
  return dayjs.utc(trimmed);
}

/**
 * Human-readable duration since `status_updated_at` for a milestone status tooltip.
 * Returns null when the timestamp is missing or invalid.
 */
export function formatTimeInMilestoneStatus(
  statusUpdatedAt: string | null | undefined,
  statusLabel: string,
  now: dayjs.ConfigType = dayjs()
): string | null {
  if (!statusUpdatedAt?.trim()) return null;

  const started = parseMilestoneStatusUpdatedAt(statusUpdatedAt);
  if (!started.isValid()) return null;

  const current = dayjs(now);
  const minutes = current.diff(started, 'minute');
  if (minutes < 0) return null;

  if (minutes < 60) {
    return `${statusLabel} for less than an hour`;
  }

  const hours = current.diff(started, 'hour');
  if (hours < 24) {
    return `${statusLabel} for ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }

  const days = current.diff(started, 'day');
  if (days < 30) {
    return `${statusLabel} for ${days} ${days === 1 ? 'day' : 'days'}`;
  }

  const months = current.diff(started, 'month');
  if (months < 12) {
    return `${statusLabel} for ${months} ${months === 1 ? 'month' : 'months'}`;
  }

  const years = current.diff(started, 'year');
  return `${statusLabel} for ${years} ${years === 1 ? 'year' : 'years'}`;
}
